"use client";
import BFLImage from "@repo/shared-ui/BFLImage";
import {
  selectIsAuthSuccessOrPerpetual,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import { FC, useCallback, useState } from "react";
import WatchlistModal from "../Watchlist/WatchlistModal";
import { StockLogoProps, WatchlistData } from "./types";
import { dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

export const StockLogo: FC<StockLogoProps> = ({
  logoURL,
  defaultImage,
  symbol,
  stock,
  onModalStateChange,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  cta_text,
  section_title
}) => {
  const fallback =
    defaultImage ??
    "https://cms-assets.bajajfinserv.in/is/image/bajajfinancestage/stocksplaceholder";
  const validLogoURL = logoURL?.trim() ?? "";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWatchlistData, setSelectedWatchlistData] =
    useState<WatchlistData | null>(null);
  const loginStatus = useAppSelector(selectIsAuthSuccessOrPerpetual);

  const handleBookmarkClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const data = {
        section_title: section_title,
        component: section_title,
        event: dlEventConstants.CLICK,
        element: dlElementConstants.WATCH_LIST_ICON_CLICK,
        cta_text: cta_text,
        event_type: dlEventConstants.ICON_CLICK,
      };

      const cleverTapObj = {
        EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
        EP_TILE_CLICKED: stock?.fullName ?? "",
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
      };
      dataLayer("", data, cleverTapObj);

      if (!loginStatus) {
        store.dispatch(setIsCommonSsoModalVisible(true));
        return;
      }
      setIsModalOpen(true);
      onModalStateChange?.(true);
    },
    [loginStatus, stock, onModalStateChange],
  );

  const handleCloseModal = useCallback(async () => {
    setIsModalOpen(false);
    setSelectedWatchlistData(null);
    onModalStateChange?.(false);
  }, [onModalStateChange, selectedWatchlistData]);

  const selectedStock = stock
    ? {
      exchange: stock.exchange,
      token: stock.token,
      symbol: stock.symbol,
      fullName: stock.fullName,
    }
    : null;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="h-[60px] w-[60px] max-md:h-[42px] max-md:w-[42px]">
          <BFLImage
            src={validLogoURL || fallback}
            alt={`${symbol} logo`}
            width={60}
            quality={20}
            height={60}
            className="h-[60px] w-[60px] rounded-full border border-[#F2F2F2] max-md:h-[42px] max-md:w-[42px]"
          />
        </div>

        <button
          onClick={handleBookmarkClick}
          type="button"
          className="pointer-events-auto rounded"
          aria-label="Add to watchlist"
        >
          <span className="flex h-[16px] w-[16px] lg:h-[20px] lg:w-[20px] cursor-pointer items-center justify-center transition-opacity hover:opacity-70">
            <i className="icomoon lg:text-[20px] text-[18px] before:content-['\e95C']" />
          </span>
        </button>
      </div>

      {selectedStock && modalTitle && (
        <WatchlistModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          selectedStock={selectedStock}
          successMsg={successMsg}
          errorMsg={errorMsg}
          modalTitle={modalTitle}
          modalBtnCta={modalBtnCta}
          onWatchlistSelect={setSelectedWatchlistData}
        />
      )}
    </>
  );
};
